import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["item", "container"]
  static values = { 
    animation: { type: Number, default: 250 },
    easing: { type: String, default: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" },
    active: { type: Boolean, default: false }
  }

  connect() {
    
    this.setupSortable()
    this.setupResponsiveFeatures()
    this.updateSortableState()
    
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
    this.removeTouchListeners()
  }

  // Método público para toggle do modo reorder
  toggle() {
    
    this.activeValue = !this.activeValue
    this.updateSortableState()
    this.updateCardsAnimation()
    this.dispatchToggleEvent()
    
  }

  // Ativar modo reorder
  activate() {
    this.activeValue = true
    this.updateSortableState()
    this.updateCardsAnimation()
    this.dispatchToggleEvent()
  }

  // Desativar modo reorder
  deactivate() {
    this.activeValue = false
    this.updateSortableState()
    this.updateCardsAnimation()
    this.dispatchToggleEvent()
  }

  // Atualiza o estado do sortable
  updateSortableState() {
    
    if (this.sortable) {
      this.sortable.option("disabled", !this.activeValue)
    } else {
    }
    
    // Atualiza data attribute para CSS
    this.element.dataset.reorderActive = this.activeValue
    
    // Atualiza classes
    if (this.activeValue) {
      this.element.classList.add("reorder-mode")
    } else {
      this.element.classList.remove("reorder-mode")
    }
    
  }

  // Controla animação dos cards
  updateCardsAnimation() {
    const cards = this.element.querySelectorAll('.vehicle-card')
    
    if (this.activeValue) {
      // Ativa animação de "dance"
      cards.forEach((card, index) => {
        // Delay escalonado para efeito wave
        setTimeout(() => {
          card.classList.add('wiggle-animation')
        }, index * 50)
      })
    } else {
      // Remove animação
      cards.forEach((card, index) => {
        card.classList.remove('wiggle-animation')
      })
    }
  }

  // Dispatch evento customizado
  dispatchToggleEvent() {
    const detail = {
      active: this.activeValue,
      timestamp: new Date().toISOString()
    }
    
    this.dispatch("toggle", { detail })
  }

  setupSortable() {
    this.sortable = Sortable.create(this.element, {
      animation: this.animationValue,
      easing: this.easingValue,
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      forceFallback: false,
      fallbackTolerance: 0,
      touchStartThreshold: 0,
      delay: 0,
      delayOnTouchOnly: true,
      preventOnFilter: false,
      removeCloneOnHide: true,
      
      // Configurações de contenção
      group: {
        name: "vehicles",
        pull: false,
        put: false
      },
      
      // Callbacks para melhor UX
      onChoose: this.onChoose.bind(this),
      onUnchoose: this.onUnchoose.bind(this),
      onStart: this.onStart.bind(this),
      onEnd: this.onEnd.bind(this),
      onAdd: this.onAdd.bind(this),
      onUpdate: this.onUpdate.bind(this),
      onSort: this.onSort.bind(this),
      onRemove: this.onRemove.bind(this),
      onMove: this.onMove.bind(this)
    })
  }

  setupResponsiveFeatures() {
    // Suporte a touch devices
    this.element.style.touchAction = "none"
    
    // Previne zoom em dispositivos móveis durante drag
    this.preventZoomDuringDrag()
  }

  preventZoomDuringDrag() {
    let lastTouchEnd = 0
    
    this.element.addEventListener('touchend', (event) => {
      const now = (new Date()).getTime()
      if (now - lastTouchEnd <= 300) {
        event.preventDefault()
      }
      lastTouchEnd = now
    }, false)
  }

  onChoose(event) {
    
    // Card foi selecionado para drag
    const item = event.item
    item.style.cursor = "grabbing"
    
    // Adiciona feedback visual
    this.element.classList.add("sorting-active")
    
    // Remove seleções de texto
    this.clearTextSelections()
    
  }

  onUnchoose(event) {
    // Card foi desselecionado
    const item = event.item
    item.style.cursor = "grab"
    
    this.element.classList.remove("sorting-active")
  }

  onStart(event) {
    
    const item = event.item
    
    // Bloqueia seleção de texto globalmente
    document.body.style.userSelect = "none"
    document.body.style.webkitUserSelect = "none"
    document.body.style.mozUserSelect = "none"
    document.body.style.msUserSelect = "none"
    
    // Adiciona classe de estado ativo
    document.body.classList.add("vehicle-dragging")
    
    // Feedback visual no container
    this.element.classList.add("drag-active")
    
    // Cursor grabbing
    item.style.cursor = "grabbing"
    
    // Adiciona identificação visual dos slots disponíveis
    this.highlightDropZones()
    
    // Previne scroll durante drag em mobile
    this.preventScroll = (e) => e.preventDefault()
    document.addEventListener('touchmove', this.preventScroll, { passive: false })
    
  }

  onEnd(event) {
    const item = event.item
    
    // Restaura seleção de texto
    document.body.style.userSelect = ""
    document.body.style.webkitUserSelect = ""
    document.body.style.mozUserSelect = ""
    document.body.style.msUserSelect = ""
    
    // Remove classes de estado
    document.body.classList.remove("vehicle-dragging")
    this.element.classList.remove("drag-active", "sorting-active")
    
    // Cursor volta ao normal
    item.style.cursor = "grab"
    
    // Remove highlight dos drop zones
    this.removeDropZoneHighlight()
    
    // Remove prevenção de scroll
    if (this.preventScroll) {
      document.removeEventListener('touchmove', this.preventScroll)
      this.preventScroll = null
    }
    
    // Log da reordenação
    this.logNewOrder(event)
    
    // Feedback visual de sucesso
    this.showSuccessFeedback(item)
    
    // Dispatch evento customizado
    this.dispatchSortEvent(event)
  }

  onMove(event) {
    // Controla onde o item pode ser solto
    const related = event.related
    const dragged = event.dragged
    
    // Só permite drop dentro do container de veículos
    return related.closest('.vehicles-list') !== null
  }

  onAdd(event) {
    // Callback quando item é adicionado (não usado aqui, mas disponível)
  }

  onUpdate(event) {
    // Callback quando lista é atualizada
  }

  onSort(event) {
    // Callback quando ocorre reordenação
  }

  onRemove(event) {
    // Callback quando item é removido (não usado aqui, mas disponível)
  }

  highlightDropZones() {
    // Adiciona indicadores visuais dos slots de drop disponíveis
    const items = Array.from(this.element.children)
    items.forEach((item, index) => {
      if (!item.classList.contains('vehicle-drag')) {
        item.classList.add('drop-zone-available')
      }
    })
  }

  removeDropZoneHighlight() {
    // Remove indicadores visuais
    const items = Array.from(this.element.children)
    items.forEach(item => {
      item.classList.remove('drop-zone-available')
    })
  }

  clearTextSelections() {
    // Remove qualquer seleção de texto ativa
    if (document.selection && document.selection.empty) {
      document.selection.empty()
    } else if (window.getSelection) {
      const selection = window.getSelection()
      selection.removeAllRanges()
    }
  }

  logNewOrder(event) {
    const vehicleIds = Array.from(this.element.children).map((card, index) => ({
      id: card.dataset.vehicleId,
      position: index + 1,
      plate: card.querySelector('.vehicle-plate')?.textContent || `Vehicle ${index + 1}`
    }))
    
  }

  showSuccessFeedback(item) {
    // Feedback visual sutil de sucesso
    item.classList.add('reorder-success')
    setTimeout(() => {
      item.classList.remove('reorder-success')
    }, 800)
  }

  dispatchSortEvent(event) {
    const detail = {
      vehicleIds: Array.from(this.element.children).map(card => card.dataset.vehicleId),
      oldIndex: event.oldIndex,
      newIndex: event.newIndex,
      element: event.item,
      timestamp: new Date().toISOString()
    }
    
    this.dispatch("sorted", { detail })
  }

  removeTouchListeners() {
    if (this.preventScroll) {
      document.removeEventListener('touchmove', this.preventScroll)
    }
  }
}